import { useState } from 'react'
import { message, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { userService, type UpdateUserRequest } from '../../../shared/api/services/users.service'
import type { User } from '../types/user.types'

export const useUserActions = () => {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingUser, setEditingUser] = useState<User | null>(null)
    const queryClient = useQueryClient()

    // Update user mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) => 
            userService.updateUser(id, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] })
            message.success('User updated successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to update user')
        },
    })

    // Delete user mutation
    const deleteMutation = useMutation({
        mutationFn: (id: number) => userService.deleteUser(id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] })
            message.success('User deleted successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to delete user')
        },
    })

    // Suspend/Unsuspend user mutation
    const toggleSuspendMutation = useMutation({
        mutationFn: ({ id, suspend }: { id: number; suspend: boolean }) => 
            suspend ? userService.suspendUser(id) : userService.unsuspendUser(id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] })
            message.success('User status updated successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to update user status')
        },
    })

    // Bulk delete mutation
    const bulkDeleteMutation = useMutation({
        mutationFn: (ids: number[]) => userService.bulkDelete(ids),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] })
            message.success('Users deleted successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to delete users')
        },
    })

    // Bulk suspend mutation
    const bulkSuspendMutation = useMutation({
        mutationFn: (ids: number[]) => userService.bulkUpdateStatus(ids, 'suspended'),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] })
            message.success('Users suspended successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to suspend users')
        },
    })

    const handleEditUser = (user: User) => {
        setEditingUser(user)
        setIsFormModalOpen(true)
    }

    const handleSuspendUser = (user: User) => {
        const action = user.status === 'suspended' ? 'activate' : 'suspend'
        Modal.confirm({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
            content: `Are you sure you want to ${action} ${user.full_name}?`,
            okText: action === 'suspend' ? 'Suspend' : 'Activate',
            okType: action === 'suspend' ? 'danger' : 'primary',
            onOk: () => {
                toggleSuspendMutation.mutate({ id: user.user_id, suspend: action === 'suspend' })
            },
        })
    }

    const handleDeleteUser = (user: User) => {
        Modal.confirm({
            title: 'Delete User',
            content: `Are you sure you want to delete ${user.full_name}? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(user.user_id)
            },
        })
    }

    const handleBulkSuspend = (userIds: React.Key[], onSuccess?: () => void) => {
        const ids = userIds.map(id => Number(id))
        Modal.confirm({
            title: 'Suspend Selected Users',
            content: `Are you sure you want to suspend ${userIds.length} users?`,
            okText: 'Suspend',
            okType: 'danger',
            onOk: async () => {
                await bulkSuspendMutation.mutateAsync(ids)
                onSuccess?.()
            },
        })
    }

    const handleBulkDelete = (userIds: React.Key[], onSuccess?: () => void) => {
        const ids = userIds.map(id => Number(id))
        Modal.confirm({
            title: 'Delete Selected Users',
            content: `Are you sure you want to delete ${userIds.length} users? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                await bulkDeleteMutation.mutateAsync(ids)
                onSuccess?.()
            },
        })
    }

    const handleFormSubmit = (values: any) => {
        // Only update existing user (no creation from admin panel)
        if (editingUser) {
            updateMutation.mutate({
                id: editingUser.user_id,
                data: {
                    full_name: values.full_name,
                    email: values.email,
                    status: values.status,
                },
            })
        }
    }

    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        isFormModalOpen,
        editingUser,
        handleEditUser,
        handleSuspendUser,
        handleDeleteUser,
        handleBulkSuspend,
        handleBulkDelete,
        handleFormSubmit,
        closeFormModal,
        isSubmitting: updateMutation.isPending,
    }
}
